import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface PlayerState {
  isPlaying: boolean
  sampleSongFlag: boolean
  isMuted: boolean
  currentTime: number
  seekTime: number | null
  duration: number
  shouldShowMiniPlayer: boolean
  isLoading: boolean
  isDraggingProgressBar: boolean
  song: unknown
  release: unknown
  volume: number
  previousVolume: number
  autoPlay: boolean
  // Set when the loaded song will start from the playbar without having
  // reported Play Song yet (restored from the last session or ended)
  pendingPlayTrigger: string | null
}

const initialState: PlayerState = {
  isPlaying: false,
  sampleSongFlag: false,
  isMuted: false,
  currentTime: 0.0,
  seekTime: null,
  duration: 1000000.0,
  shouldShowMiniPlayer: false,
  isLoading: false,
  isDraggingProgressBar: false,
  song: null,
  release: null,
  volume: 50,
  previousVolume: 50,
  autoPlay: true,
  pendingPlayTrigger: null,
}

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    updatePlayerState: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload
    },
    updateSampleSongFlag: (state, action: PayloadAction<boolean>) => {
      state.sampleSongFlag = action.payload
    },
    updateMuteState: (state, action: PayloadAction<boolean>) => {
      state.isMuted = action.payload
    },
    updateDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload
    },
    updateCurrentTime: (state, action: PayloadAction<number>) => {
      if (state.isDraggingProgressBar || state.isLoading) {
        return
      }
      state.currentTime = action.payload
    },
    seekTimeRequest: (state, action: PayloadAction<number>) => {
      state.seekTime = action.payload
      state.currentTime = action.payload
    },
    seekTimeFinished: (state) => {
      state.seekTime = null
    },
    toggleMiniPlayer: (state, action: PayloadAction<boolean>) => {
      state.shouldShowMiniPlayer = action.payload
    },
    updateLoadingPlayer: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    updateDraggingProgressBar: (state, action: PayloadAction<boolean>) => {
      state.isDraggingProgressBar = action.payload
    },
    selectSong: (state, action: PayloadAction<unknown>) => {
      state.song = action.payload
    },
    selectRelease: (state, action: PayloadAction<unknown>) => {
      state.release = action.payload
    },
    reset: () => initialState,
    resetPlayer: (state) => {
      return { ...initialState, autoPlay: state.autoPlay }
    },
    updateVolume: (state, action: PayloadAction<number>) => {
      state.volume = action.payload
    },
    updatePreviousVolume: (state, action: PayloadAction<number>) => {
      state.previousVolume = action.payload
    },
    toggleAutoPlay: (state, action: PayloadAction<boolean>) => {
      state.autoPlay = action.payload
    },
    updatePendingPlayTrigger: (state, action: PayloadAction<string | null>) => {
      state.pendingPlayTrigger = action.payload
    },
  },
})

export const playerActions = playerSlice.actions
export const playerReducer = playerSlice.reducer
export default playerActions
